import type { DiagnosticTabProps } from "../DiagnosticSection";
import { AdherenceCard } from "./environment/AdherenceCard";
import { ChartOfAccountsCard } from "./environment/ChartOfAccountsCard";
import { DigitalAccessCard } from "./environment/DigitalAccessCard";
import { DocumentVolumesCard } from "./environment/DocumentVolumesCard";
import { EnvironmentChips } from "./environment/EnvironmentChips";
import { EnvironmentOverviewCard } from "./environment/EnvironmentOverviewCard";
import { UsageProfileCard } from "./environment/UsageProfileCard";
import { WricefCard } from "./environment/WricefCard";
import { AreaGate } from "../projectAreas";

/**
 * Ato 1 · O ambiente — "Olhamos dentro. Isto é o que existe." (#852)
 *
 * O ato de abertura mostra o retrato do sistema e **nenhum valor financeiro**.
 * Isso é regra de roteiro, não acaso: o dinheiro é o clímax e mora no Ato 5, que
 * fica trancado até o vendedor abrir. Os cards de veredito, cobertura e aderência
 * desceram daqui para o Ato 2 na #852, porque eles interpretam o retrato em vez
 * de mostrá-lo.
 *
 * **Apresentacional por contrato**: recebe `sections`, `snapshots` e `validation`
 * por props e não chama hook de dado. É o que torna
 * `render(<EnvironmentAct sections={fixture} />)` possível sem mock — não há MSW
 * no repo, por decisão de arquitetura do S-059.
 *
 * O ato lê `sections` **inteiro**, e não só as chaves que declara: os agregados
 * moram em `license_measurement`, `wricef_catalog`, `org_structure` e
 * `cost_breakdown`. Isso é legítimo porque cada card é um `DiagnosticSection` com
 * o **seu** envelope, e portanto declara a própria origem no rodapé, herda o
 * banner de `partial`/`truncated`, a linha de amostragem e o `ClosedBody` dos
 * estados sem dado.
 *
 * Nenhum número é calculado aqui: tudo vem de `diagnosticData.ts`, e tudo passa
 * por `sectionCounter` — `—` quando desconhecido, nunca `0`.
 */
export function EnvironmentAct({ sections, snapshots, validation }: DiagnosticTabProps) {
  return (
    // A ordem é a que o Marco pediu na reunião de 19/10/2026, antes da
    // apresentação da AL5 (#1476): a visão geral, o plano de contas numa linha
    // própria (é grande demais para meia linha), o perfil de uso ao lado do
    // WRICEF, o Digital Access e o volume real numa linha própria. A Vitalidade
    // saiu até o cálculo ligar a execução do ST03N ao objeto; o componente
    // continua, para voltar corrigido.
    <div className="grid grid-cols-1 gap-4">
      <EnvironmentChips snapshots={snapshots} />

      <EnvironmentOverviewCard sections={sections} snapshots={snapshots} validation={validation} />

      {/* EF-FUNC-011 (RF-07): o frame abre o número do tile em Processo,
          Desenvolvimentos e Consolidado, logo abaixo dele, como na MOC. */}
      <AreaGate area="adherence">
        <AdherenceCard sections={sections} snapshots={snapshots} validation={validation} />
      </AreaGate>

      <div data-testid="environment-row-coa">
        <ChartOfAccountsCard sections={sections} snapshots={snapshots} validation={validation} />
      </div>

      <div data-testid="environment-row-people" className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <UsageProfileCard sections={sections} snapshots={snapshots} />
        <AreaGate area="architect">
          <WricefCard sections={sections} snapshots={snapshots} validation={validation} />
        </AreaGate>
      </div>

      {/* EF-FUNC-005 (#1346 fatia 9): a tabela por item da SAP, em largura cheia. */}
      <DigitalAccessCard sections={sections} snapshots={snapshots} validation={validation} />

      <div data-testid="environment-row-volumes">
        <DocumentVolumesCard sections={sections} snapshots={snapshots} validation={validation} />
      </div>
    </div>
  );
}
